import { exploreTrees } from "api/trees";
import type { ExploreTreeDto, PaginatedList } from "api/types";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { getTreeViewPath } from "routes/Paths";

import styles from "./TrendingTrees.module.css";

/** Cards shown in the landing grid; also the number of placeholders held while loading. */
const CARD_COUNT = 4;

const placeholderKeys = Array.from({ length: CARD_COUNT }, (_: unknown, index: number) => index);

type TrendingTreesProps = {
	/** Class for the grid wrapper, so the section keeps the landing page's own layout. */
	className: string;
	/** Class for a loading/empty placeholder tile. */
	placeholderClassName: string;
};

/**
 * The most-starred public trees, as ordinary links.
 *
 * These are the homepage's only route into a tree, for a reader and for a crawler alike: real
 * `<a href="/trees/…">` anchors with no sign-in redirect in between, so a search engine following
 * links from the site root can reach individual trees. Fetched anonymously — the explore endpoint
 * returns the public pool without a token.
 *
 * Failure and emptiness both fall back to the placeholder tiles rather than an error: this is the
 * bottom of a marketing page, and a missing feed should never be what a first-time visitor reads.
 */
const TrendingTrees = ({ className, placeholderClassName }: TrendingTreesProps) => {
	const { t } = useTranslation(["landingPage", "explorePage"]);
	const [trees, setTrees] = useState<ExploreTreeDto[]>([]);

	useEffect(() => {
		let isCurrent = true;

		exploreTrees({ feed: "Trending", pageIndex: 1, pageSize: CARD_COUNT }, "")
			.then((response: PaginatedList<ExploreTreeDto>): void => {
				if (isCurrent) {
					setTrees(response.items);
				}
			})
			.catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, []);

	if (trees.length === 0) {
		return (
			<div className={className}>
				{placeholderKeys.map((key: number) => (
					<div
						key={key}
						aria-label={t("landingPage:trending.placeholder")}
						className={placeholderClassName}
						role="img"
					/>
				))}
			</div>
		);
	}

	return (
		<div className={className}>
			{trees.map((tree: ExploreTreeDto) => (
				<Link key={tree.id} className={styles.card} to={getTreeViewPath(tree.id)}>
					{tree.coverImageUrl && <img alt="" className={styles.cover} src={tree.coverImageUrl} />}
					<span className={styles.body}>
						<span className={styles.title}>{tree.title}</span>
						<span className={styles.meta}>
							{t("landingPage:trending.byOwner", { name: tree.ownerName })}
						</span>
					</span>
				</Link>
			))}
		</div>
	);
};

export default TrendingTrees;
